import { useEffect, useRef, useState } from "react";

import {
  Box,
  Checkbox,
  FormControl,
  FormGroup,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import QuestionStatus from "./QuestionStatus";
import SurveyEntry_Distribution from "./SurveyEntry_Distribution";
import SurveyEntry_OutOfStock from "./SurveyEntry_OutOfStock";
import SurveyImages from "./SurveyImages";
import SurveyEntry_Variability from "./SurveyEntry_Variability";
import Signature from "./Signature";
import SurveyEntry_MultiSelect from "./SurveyEntry_MultiSelect";
import SurveyEntry_Time from "./SurveyEntry_Time";
import SurveyEntry_Date from "./SurveyEntry_Date";
import DescriptionDisplay from "./DescriptionDisplay";

import useSurvey from "../../hooks/useSurvey";
import { useDebounce } from "../../hooks/useDebounce";

const SurveyQuestion = ({
  section,
  question,
  questions,
  location,
  call,
  sku,
  answer,
  answers,
  onChange,
  isDisabled,
  testMode = false,
}) => {
  const { showQuestion } = useSurvey();
  const [show, setShow] = useState(true);
  const [rangeError, setRangeError] = useState(null);
  const syncingFromAnswerRef = useRef(false);
  const isEditingRef = useRef(false);
  const latestLocalValueRef = useRef("");

  // local state for text-like inputs
  const [localValue, setLocalValue] = useState(answer?.value ?? "");
  const debouncedLocalValue = useDebounce(localValue, 450);

  useEffect(() => {
    latestLocalValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    // Keep local input value in sync when an answer is loaded/changed from server or parent state
    if (isEditingRef.current) return;

    const incomingValue = answer ? String(answer.value ?? "") : "";
    if (incomingValue === String(localValue ?? "")) return;

    syncingFromAnswerRef.current = true;
    setLocalValue(incomingValue);
  }, [answer, question, sku]);

  useEffect(() => {
    determineQuestionShown();
  }, [
    answer,
    answers,
    question,
    sku?.id,
    call?.id,
    location?.id,
    location?.bannerId,
    location?.retailerId,
    location?.province,
    location?.address?.province,
  ]);

  const parseNumeric = (val) => {
    if (val === "" || val === null || val === undefined) return null;
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
  };

  const commitLocalValue = (nextInputValue = localValue) => {
    if (syncingFromAnswerRef.current) {
      syncingFromAnswerRef.current = false;
      return;
    }

    if (question.questionType === "text") {
      const currentValue = answer ? String(answer.value ?? "") : "";
      const nextValue = String(nextInputValue ?? "");
      if (currentValue === nextValue) return;
      onChange(nextValue);
      return;
    }

    if (
      question.questionType === "number" ||
      question.questionType === "currency"
    ) {
      const parsed = parseNumeric(nextInputValue);

      if (nextInputValue !== "" && parsed === null) {
        setRangeError("Enter a valid number");
        return;
      }

      const belowMin =
        parsed !== null &&
        question?.minValue !== null &&
        question?.minValue !== undefined &&
        parsed < question.minValue;
      const aboveMax =
        parsed !== null &&
        question?.maxValue !== null &&
        question?.maxValue !== undefined &&
        parsed > question.maxValue;

      if (belowMin || aboveMax) {
        const message = belowMin
          ? `Minimum value is ${question.minValue}`
          : `Maximum value is ${question.maxValue}`;
        setRangeError(message);
        return;
      }

      setRangeError(null);

      const currentNumber = parseNumeric(answer?.value);
      if (currentNumber === parsed) return;

      onChange(parsed);
    }
  };

  useEffect(() => {
    if (isDisabled) return;

    if (
      question.questionType !== "text" &&
      question.questionType !== "number" &&
      question.questionType !== "currency"
    ) {
      return;
    }

    commitLocalValue(debouncedLocalValue);
  }, [debouncedLocalValue, question.questionType, isDisabled]);

  useEffect(() => {
    return () => {
      // Avoid dispatching new answer writes during unmount; when logic pruning
      // removes a question, an unmount-triggered save can race with deletion.
      syncingFromAnswerRef.current = false;
    };
  }, []);

  const handleTextLikeFocus = () => {
    isEditingRef.current = true;
  };

  const handleTextLikeBlur = () => {
    isEditingRef.current = false;
    commitLocalValue();
  };

  const determineQuestionShown = async () => {
    const showme = await showQuestion({
      question,
      questions,
      answer,
      call,
      location,
      answers,
      skuId: sku?.id,
    });

    setShow(showme);
  };

  const isSelected = (optionId) => {
    if (!answer) return false;
    if (!answer.value) return false;
    if (answer.value.includes(optionId)) return true;

    return false;
  };

  const handleChecked = (id) => {
    let currentOptions = Array.isArray(answer?.value)
      ? [...answer.value]
      : null;
    if (!currentOptions) {
      onChange([id]);
      return;
    }
    const optionIndex = currentOptions.findIndex((option) => option === id);
    if (optionIndex === -1) {
      onChange([...currentOptions, id]);
      return;
    }

    currentOptions.splice(optionIndex, 1);
    onChange(currentOptions);
  };

  if (!show) return null;

  return (
    <>
      <Box
        sx={{
          padding: "4px",
          backgroundColor: "#fff",
          borderRadius: "4px",
          marginTop: "8px",
        }}
      >
        <Stack>
          <Box sx={{ display: "flex" }}>
            <QuestionStatus
              question={question}
              answer={answer}
            />
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: "bold",
              }}
            >
              {question.name}
            </Typography>
          </Box>

          <DescriptionDisplay item={question} />

          {question.variability && (
            <Box>
              <SurveyEntry_Variability
                section={section}
                question={question}
                call={call}
              />
            </Box>
          )}

          {question.questionType === "text" && (
            <TextField
              size="small"
              type="text"
              fullWidth
              multiline
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onFocus={handleTextLikeFocus}
              onBlur={handleTextLikeBlur}
              disabled={isDisabled}
            />
          )}
          {question.questionType === "number" && (
            <TextField
              size="small"
              type="number"
              fullWidth
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onFocus={handleTextLikeFocus}
              onBlur={handleTextLikeBlur}
              disabled={isDisabled}
              error={Boolean(rangeError)}
              helperText={rangeError || undefined}
              inputProps={{
                min: question.minValue ?? undefined,
                max: question.maxValue ?? undefined,
                step: 1,
              }}
            />
          )}
          {question.questionType === "currency" && (
            <TextField
              size="small"
              type="number"
              fullWidth
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onFocus={handleTextLikeFocus}
              onBlur={handleTextLikeBlur}
              disabled={isDisabled}
              error={Boolean(rangeError)}
              helperText={rangeError || undefined}
              inputProps={{
                min: question.minValue ?? undefined,
                max: question.maxValue ?? undefined,
                step: 0.01,
              }}
            />
          )}
          {question.questionType === "date" && (
            <SurveyEntry_Date
              value={answer ? answer.value : null}
              onChange={(newValue) => onChange(newValue)}
              isDisabled={isDisabled}
            />
          )}
          {question.questionType === "time" && (
            <SurveyEntry_Time
              value={answer ? answer.value : null}
              onChange={(newValue) => onChange(newValue)}
              isDisabled={isDisabled}
            />
          )}
          {question.questionType === "image" && (
            <Box>
              <SurveyImages
                question={question}
                answer={answer}
                onChange={(newValue) => onChange(newValue)}
                disabled={isDisabled}
                testMode={testMode}
              />
            </Box>
          )}
          {question.questionType === "signature" && (
            <Box>
              <Signature
                answer={answer}
                onChange={(newValue) => onChange(newValue)}
                isDisabled={isDisabled}
              />
            </Box>
          )}
          {question.questionType === "checkbox" &&
            Array.isArray(question.options) && (
              <FormControl>
                <FormGroup>
                  {question.options.map((option, index) => (
                    <FormControlLabel
                      key={index}
                      value={option.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={isSelected(option.id)}
                          onChange={(e) =>
                            handleChecked(option.id, e.target.checked)
                          }
                          disabled={isDisabled}
                        />
                      }
                      label={option.name}
                      labelPlacement="end"
                      sx={{
                        ".MuiFormControlLabel-label": {
                          fontSize: "10px",
                        },
                      }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}
          {question.questionType === "radio" &&
            Array.isArray(question.options) && (
              <FormControl>
                <RadioGroup
                  value={answer ? answer.value : ""}
                  onChange={(e) => onChange(e.target.value)}
                >
                  {question.options.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={
                        <Radio
                          size="small"
                          disabled={isDisabled}
                        />
                      }
                      label={option.name}
                      labelPlacement="end"
                      sx={{
                        ".MuiFormControlLabel-label": {
                          fontSize: "10px",
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}

          {question.questionType === "multiselect" && (
            <SurveyEntry_MultiSelect
              question={question}
              value={answer ? answer.value : []}
              onChange={(newValue) => onChange(newValue)}
              isDisabled={isDisabled}
            />
          )}
          {question.questionType === "select" && (
            <Select
              size="small"
              fullWidth
              value={answer ? answer.value : ""}
              onChange={(e) => onChange(e.target.value)}
              disabled={isDisabled}
            >
              {question.options.map((option, index) => (
                <MenuItem
                  key={index}
                  value={option.id}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          )}

          {question.questionType === "distribution" && (
            <SurveyEntry_Distribution
              value={answer ? answer.value : null}
              onChange={(newValue) => onChange(newValue)}
              question={question}
              isDisabled={isDisabled}
              testMode={testMode}
            />
          )}

          {question.questionType === "outofstock" && (
            <SurveyEntry_OutOfStock
              value={answer ? answer.value : null}
              onChange={(newValue) => onChange(newValue)}
              question={question}
              isDisabled={isDisabled}
            />
          )}
        </Stack>
      </Box>
    </>
  );
};

export default SurveyQuestion;
